/**
 * <FormField> — RHF + Zod adapter per CONTEXT D-08.
 *
 * The primitives (`<Input>`, `<Select>`, `<Checkbox>`, `<DateField>`, ...) stay
 * React-Hook-Form-*agnostic* so they remain renderable in Storybook without
 * an RHF context and so we can swap RHF later without touching every primitive.
 * This adapter is the bridge: it reads `Controller` from `react-hook-form` and
 * exposes a `render(field)` slot where the consumer plugs in the primitive.
 *
 * Usage (inside a `<FormProvider>` or with an explicit `control` prop):
 *
 *   <FormField
 *     name="email"
 *     label="Email"
 *     required
 *     render={(field) => (
 *       <Input type="email" {...field} invalid={field.invalid} />
 *     )}
 *   />
 *
 * Accessibility:
 *   - Label is associated with the rendered control via `htmlFor` + `id`.
 *   - `aria-describedby` wires both the description text (if provided) and
 *     the error paragraph (when present) to the control.
 *   - `aria-invalid` is propagated from RHF's fieldState.
 *   - The required-asterisk is rendered with `aria-hidden` so it doesn't
 *     double-announce — we set `aria-required` on the control instead.
 */
import * as React from "react";
import {
  Controller,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
  type ControllerRenderProps,
  type ControllerFieldState,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils";
import { FieldError } from "./FieldError";

/**
 * Schema message keys whose bundle key differs from the dotted form the
 * schema emits. Everything else resolves by the generic rule below.
 */
const FIELD_ERROR_KEY_ALIASES: Readonly<Record<string, string>> = {
  "validation.email.invalid": "validation:email",
};

/**
 * Zod/RHF error messages come through as either a stable i18n key
 * (`"shifts:drawer.validation.name_ar_min"`, `"validation:required"`), the
 * dotted namespaced form the auth schemas emit (`"validation.email.invalid"`,
 * `"auth.otp.error.format"`), or a plain literal (a hand-written English
 * string or a Zod default). Translate it when it is a known key, otherwise
 * render it as-is — so admins stop seeing raw keys, with zero regression for
 * literal messages.
 *
 * The dotted form is checked against `exists` after rewriting its first
 * segment into a namespace: `validation.required` → `validation:required`.
 * The login page shipped the literal text `validation.email.invalid` because
 * the lookup only ever tried the default namespace. The field's own label
 * rides along as `{field}` for the bundles that phrase it ("{field} is
 * required").
 */
export function resolveFieldErrorMessage(
  message: string,
  i18n: {
    exists?: (key: string) => boolean;
    t?: (key: string, options?: Record<string, unknown>) => string;
  },
  fieldLabel?: string,
  values: Record<string, string> = {},
): string {
  // react-i18next returns an empty `{}` for `i18n` when there's no configured
  // instance (e.g. a Storybook story or a test with no provider) — guard the
  // methods so the field renders the literal message instead of throwing.
  if (typeof i18n.exists !== "function" || typeof i18n.t !== "function") return message;
  const options = { ...values, ...(fieldLabel !== undefined ? { field: fieldLabel } : {}) };
  if (i18n.exists(message)) return i18n.t(message, options);
  const alias = FIELD_ERROR_KEY_ALIASES[message];
  if (alias !== undefined && i18n.exists(alias)) return i18n.t(alias, options);
  const dotted = /^([a-z][a-z0-9_-]*)\.(.+)$/.exec(message);
  if (dotted !== null) {
    const namespaced = `${dotted[1]}:${dotted[2]}`;
    if (i18n.exists(namespaced)) return i18n.t(namespaced, options);
  }
  return message;
}

export interface FormFieldRenderArg<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /** All RHF field bindings (`name`, `value`, `onChange`, `onBlur`, `ref`). */
  name: ControllerRenderProps<TFieldValues, TName>["name"];
  value: ControllerRenderProps<TFieldValues, TName>["value"];
  onChange: ControllerRenderProps<TFieldValues, TName>["onChange"];
  onBlur: ControllerRenderProps<TFieldValues, TName>["onBlur"];
  ref: ControllerRenderProps<TFieldValues, TName>["ref"];
  /** HTML id to pass to the control so the label + aria-describedby line up. */
  id: string;
  /** Error present? — convenience for the consumer's primitive. */
  invalid: boolean;
  /** `aria-describedby` token list covering description + error. */
  "aria-describedby": string | undefined;
  /** `aria-invalid` mirror of `invalid`. */
  "aria-invalid": boolean;
  /** `aria-required` mirror of the `required` prop. */
  "aria-required": boolean;
  /** Full RHF fieldState for advanced needs. */
  fieldState: ControllerFieldState;
}

export interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /** RHF field name (dot-path). */
  name: TName;
  /** Visible label. */
  label: React.ReactNode;
  /** Optional helper text rendered between the label and the control. */
  description?: React.ReactNode;
  /** Marks the field as required (visual asterisk + aria-required). */
  required?: boolean;
  /** Explicit RHF control. Omit to use the nearest `useFormContext()`. */
  control?: Control<TFieldValues>;
  /** Render the primitive. Receives the RHF field bindings + a11y tokens. */
  render: (arg: FormFieldRenderArg<TFieldValues, TName>) => React.ReactNode;
  /** Consumer className forwarded to the field container. */
  className?: string;
  /**
   * Values for the error message's placeholders, already formatted for the
   * reader (a bound such as `{max}`), so a schema key can name its limits
   * without printing Latin digits in Arabic.
   */
  errorValues?: Record<string, string>;
}

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  description,
  required,
  control,
  render,
  className,
  errorValues,
}: FormFieldProps<TFieldValues, TName>) {
  const context = useFormContext<TFieldValues>();
  const { i18n } = useTranslation();
  const resolvedControl = control ?? context?.control;
  // Stable id per field so labels/describedby line up across re-renders.
  const reactId = React.useId();
  const id = `ff-${reactId}-${String(name)}`;
  const descId = description !== undefined ? `${id}-desc` : undefined;
  const errId = `${id}-err`;

  if (!resolvedControl) {
    // Intentional runtime + build-time signal: a FormField without a control
    // means the consumer forgot `<FormProvider>` or the explicit `control`
    // prop. Surface loudly — catching this at runtime beats a silent dead
    // form in production.
    throw new Error(
      `<FormField name="${String(name)}"> requires either an explicit ` +
        "`control` prop or a surrounding <FormProvider>.",
    );
  }

  return (
    <Controller<TFieldValues, TName>
      name={name}
      control={resolvedControl}
      render={({ field, fieldState }) => {
        const invalid = fieldState.invalid;
        const describedBy =
          [descId, invalid ? errId : undefined].filter(Boolean).join(" ") || undefined;

        return (
          <div className={cn("flex flex-col gap-xs", className)}>
            <label htmlFor={id} className="text-label text-muted">
              {label}
              {required === true && (
                <span aria-hidden="true" className="text-danger ms-xs">
                  *
                </span>
              )}
            </label>
            {description !== undefined && (
              <p id={descId} className="text-label text-muted">
                {description}
              </p>
            )}
            {render({
              ...field,
              id,
              invalid,
              "aria-describedby": describedBy,
              "aria-invalid": invalid,
              "aria-required": required === true,
              fieldState,
            })}
            {invalid && fieldState.error?.message !== undefined && (
              <FieldError
                id={errId}
                message={resolveFieldErrorMessage(
                  fieldState.error.message,
                  // Narrow the instance to the two calls the resolver makes; a
                  // provider-less render hands back `{}` and stays literal.
                  {
                    ...(typeof i18n.exists === "function"
                      ? { exists: (key: string) => i18n.exists(key) }
                      : {}),
                    ...(typeof i18n.t === "function"
                      ? {
                          t: (key: string, options?: Record<string, unknown>) =>
                            String(options === undefined ? i18n.t(key) : i18n.t(key, options)),
                        }
                      : {}),
                  },
                  typeof label === "string" ? label : undefined,
                  errorValues,
                )}
              />
            )}
          </div>
        );
      }}
    />
  );
}
